import { useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Bot,
  CheckCircle2,
  Download,
  ExternalLink,
  FileSpreadsheet,
  FileUp,
  History,
  Menu,
  RefreshCw,
  Search,
  Store,
  Timer,
  Trash2,
  TrendingUp,
  Users,
  WalletCards,
  X,
} from "lucide-react";
import type {
  DouyinDataSource,
  DouyinPerformanceRecord,
  ImportDouyinPerformanceInput,
} from "@ailan/contracts";
import {
  clearDouyinDemoData,
  getDouyinDashboard,
  importDouyinPerformance,
  syncDouyinPerformance,
} from "../api/client";
import { MetricDetailModal } from "../components/MetricDetailModal";

type View = "overview" | "history";
type TrendMode = "day" | "week" | "month";
type TrendSelection = TrendMode | "custom";
type MetricKey = "receptionCount" | "inquiryConversionRate" | "satisfactionRate" | "dissatisfactionRate" | "avgReplySeconds" | "netSalesAmount";

type Summary = {
  receptionCount: number;
  dissatisfactionRate: number;
  satisfactionRate: number;
  avgReplySeconds: number;
  inquiryConversionRate: number;
  netSalesAmount: number;
};

type Bucket = {
  key: string;
  label: string;
  records: DouyinPerformanceRecord[];
};

const sourceLabels: Record<DouyinDataSource, string> = {
  DEMO: "演示数据",
  CSV: "飞鸽导入",
  MANUAL: "手工录入",
  CONNECTOR: "自动采集",
};

const metricLabels: Record<MetricKey, string> = {
  receptionCount: "已接待人数",
  inquiryConversionRate: "询单转化率",
  satisfactionRate: "工作时间满意率",
  dissatisfactionRate: "工作时间不满意率",
  avgReplySeconds: "全天平均响应",
  netSalesAmount: "净销售额",
};

function summarize(records: DouyinPerformanceRecord[]): Summary {
  const receptionCount = records.reduce((sum, item) => sum + item.receptionCount, 0);
  const weight = records.reduce((sum, item) => sum + Math.max(item.receptionCount, 1), 0);
  const weighted = (field: keyof Pick<Summary, "dissatisfactionRate" | "satisfactionRate" | "avgReplySeconds" | "inquiryConversionRate">) =>
    weight
      ? records.reduce((sum, item) => sum + item[field] * Math.max(item.receptionCount, 1), 0) / weight
      : 0;
  return {
    receptionCount,
    dissatisfactionRate: weighted("dissatisfactionRate"),
    satisfactionRate: weighted("satisfactionRate"),
    avgReplySeconds: weighted("avgReplySeconds"),
    inquiryConversionRate: weighted("inquiryConversionRate"),
    netSalesAmount: records.reduce((sum, item) => sum + item.netSalesAmount, 0),
  };
}

function formatNumber(value: number) {
  return new Intl.NumberFormat("zh-CN").format(Math.round(value));
}

function formatDecimal(value: number) {
  return (Math.trunc(value * 100) / 100).toFixed(2);
}

function formatMoney(value: number) {
  return new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency: "CNY",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.trunc(value * 100) / 100);
}

function isoDate(date: Date) {
  return date.toISOString().slice(0, 10);
}

function dateOf(value: string) {
  return new Date(`${value}T00:00:00.000Z`);
}

function bucketStart(value: string, mode: TrendMode) {
  if (mode === "day") return value;
  const date = dateOf(value);
  if (mode === "week") {
    const day = date.getUTCDay();
    date.setUTCDate(date.getUTCDate() - (day === 0 ? 6 : day - 1));
  } else date.setUTCDate(1);
  return isoDate(date);
}

function shiftDate(value: string, days: number) {
  const date = dateOf(value);
  date.setUTCDate(date.getUTCDate() + days);
  return isoDate(date);
}

function bucketLabel(start: string, mode: TrendMode) {
  const date = dateOf(start);
  if (mode === "month") return `${date.getUTCFullYear()}年${date.getUTCMonth() + 1}月`;
  if (mode === "week") return `${date.getUTCMonth() + 1}月${date.getUTCDate()}日 - ${dateOf(shiftDate(start, 6)).getUTCMonth() + 1}月${dateOf(shiftDate(start, 6)).getUTCDate()}日`;
  return `${date.getUTCMonth() + 1}月${date.getUTCDate()}日`;
}

function buildBuckets(records: DouyinPerformanceRecord[], mode: TrendMode): Bucket[] {
  const grouped = new Map<string, DouyinPerformanceRecord[]>();
  records.forEach((record) => {
    const key = bucketStart(record.periodEnd, mode);
    grouped.set(key, [...(grouped.get(key) ?? []), record]);
  });
  return [...grouped.entries()]
    .map(([key, items]) => ({ key, label: bucketLabel(key, mode), records: items }))
    .sort((a, b) => b.key.localeCompare(a.key));
}

function percentageChange(current: number, previous: number) {
  return previous ? (current - previous) / previous * 100 : 0;
}

function Delta({ current, previous, points = false, inverse = false }: { current: number; previous: number; points?: boolean; inverse?: boolean }) {
  const value = points ? current - previous : percentageChange(current, previous);
  const improved = inverse ? value <= 0 : value >= 0;
  const Icon = value >= 0 ? ArrowUpRight : ArrowDownRight;
  return <span className={`douyinDelta ${improved ? "up" : "down"}`}><Icon />较上期 {value >= 0 ? "+" : ""}{formatDecimal(value)}{points ? " 个百分点" : "%"}</span>;
}

function missingCollectionDays(batches: { periodStart: string; periodEnd: string }[]) {
  const dates = new Set(batches.filter((batch) => batch.periodStart === batch.periodEnd).map((batch) => batch.periodEnd));
  if (!dates.size) return null;
  const latest = [...dates].sort().at(-1)!;
  const start = shiftDate(latest, -13);
  return Array.from({ length: 14 }, (_, index) => shiftDate(start, index)).filter((date) => !dates.has(date));
}

function TrendMetric({ label, values, current, previous, format, inverse = false, points = false }: {
  label: string;
  values: number[];
  current: number;
  previous: number;
  format: (value: number) => string;
  inverse?: boolean;
  points?: boolean;
}) {
  const max = Math.max(...values, 1);
  return <article className="douyinTrendMetric"><div><span>{label}</span><strong>{format(current)}</strong><Delta current={current} previous={previous} inverse={inverse} points={points} /></div><div className="douyinMiniBars">{values.map((value, index) => <i key={index} style={{ height: `${Math.max(7, value / max * 100)}%` }} />)}</div></article>;
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function exportCsv(records: DouyinPerformanceRecord[]) {
  const headers = [
    "店铺",
    "客服昵称",
    "子账号名称",
    "开始日期",
    "结束日期",
    "已接待人数",
    "工作时间不满意率",
    "工作时间满意率",
    "全天平响时长",
    "询单转化率",
    "净销售额",
    "数据来源",
  ];
  const rows = records.map((record) => [
    record.storeName,
    record.agentName,
    record.subAccountName,
    record.periodStart,
    record.periodEnd,
    record.receptionCount,
    `${record.dissatisfactionRate}%`,
    `${record.satisfactionRate}%`,
    `${record.avgReplySeconds}秒`,
    `${record.inquiryConversionRate}%`,
    record.netSalesAmount,
    sourceLabels[record.dataSource],
  ]);
  const csv = `\ufeff${[headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(","))
    .join("\r\n")}`;
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  Object.assign(document.createElement("a"), {
    href: url,
    download: `抖音客服绩效-${records[0]?.periodEnd ?? "数据"}.csv`,
  }).click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function SourceTag({ source }: { source: DouyinDataSource }) {
  return <span className={`douyinSourceTag ${source.toLowerCase()}`}>{sourceLabels[source]}</span>;
}

function SourceTags({ sources }: { sources: DouyinDataSource[] }) {
  return <span className="douyinSourceTags">{sources.map((source) => <SourceTag key={source} source={source} />)}</span>;
}

export function DouyinDashboardPage({ onMenuOpen, platformNav }: { onMenuOpen: () => void; platformNav?: ReactNode }) {
  const queryClient = useQueryClient();
  const importRef = useRef<HTMLInputElement>(null);
  const detailsRef = useRef<HTMLElement>(null);
  const dashboard = useQuery({
    queryKey: ["douyin-dashboard"],
    queryFn: getDouyinDashboard,
    refetchInterval: 60_000,
  });
  const [view, setView] = useState<View>("overview");
  const [trendMode, setTrendMode] = useState<TrendSelection>("day");
  const [period, setPeriod] = useState("");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [storeFilter, setStoreFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [selectedMetric, setSelectedMetric] = useState<MetricKey>("receptionCount");
  const [metricOpen, setMetricOpen] = useState<MetricKey | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [importForm, setImportForm] = useState<ImportDouyinPerformanceInput>({
    storeName: "爱兰歌娜官方旗舰店",
    periodStart: "2026-06-01",
    periodEnd: "2026-06-30",
    fileName: "",
    csv: "",
  });

  const syncMutation = useMutation({
    mutationFn: syncDouyinPerformance,
    onSuccess: (data) => {
      queryClient.setQueryData(["douyin-dashboard"], data);
      setNotice(data.sync.enabled ? "自动采集已完成" : "已打开飞鸽登录窗口，登录后可导出并导入数据");
    },
    onError: (error) => setNotice(error instanceof Error ? error.message : "采集失败"),
  });
  const importMutation = useMutation({
    mutationFn: importDouyinPerformance,
    onSuccess: (data) => {
      queryClient.setQueryData(["douyin-dashboard"], data);
      setImportOpen(false);
      setNotice("飞鸽数据已提取并生成整理表");
    },
    onError: (error) => setNotice(error instanceof Error ? error.message : "导入失败"),
  });
  const clearDemoMutation = useMutation({
    mutationFn: clearDouyinDemoData,
    onSuccess: (nextData) => {
      queryClient.setQueryData(["douyin-dashboard"], nextData);
      setPeriod("");
      setNotice("演示数据已清除，CSV、手工和自动采集数据均已保留");
    },
    onError: (error) => setNotice(error instanceof Error ? error.message : "清理失败"),
  });

  const data = dashboard.data;
  const recordsForStore = useMemo(() => (data?.records ?? []).filter((record) =>
    storeFilter === "ALL" || record.storeName === storeFilter), [data, storeFilter]);
  const availableDates = useMemo(() => [...new Set(recordsForStore.map((record) => record.periodEnd))].sort(), [recordsForStore]);
  const earliestDate = availableDates[0] ?? "";
  const latestDate = availableDates.at(-1) ?? "";
  const defaultCustomStart = availableDates[Math.max(availableDates.length - 7, 0)] ?? earliestDate;
  const customStart = customRange.start || defaultCustomStart;
  const customEnd = customRange.end || latestDate;
  const regularBuckets = useMemo(() => buildBuckets(recordsForStore, trendMode === "custom" ? "day" : trendMode), [recordsForStore, trendMode]);
  const customBucket = useMemo<Bucket>(() => ({
    key: `custom:${customStart}:${customEnd}`,
    label: `${customStart} 至 ${customEnd}`,
    records: recordsForStore.filter((record) => record.periodEnd >= customStart && record.periodEnd <= customEnd),
  }), [customEnd, customStart, recordsForStore]);
  const customPreviousBucket = useMemo<Bucket>(() => {
    const days = customStart && customEnd ? Math.round((dateOf(customEnd).getTime() - dateOf(customStart).getTime()) / 86_400_000) + 1 : 0;
    const previousEnd = days ? shiftDate(customStart, -1) : "";
    const previousStart = days ? shiftDate(previousEnd, -(days - 1)) : "";
    return {
      key: `custom-previous:${previousStart}:${previousEnd}`,
      label: `${previousStart} 至 ${previousEnd}`,
      records: recordsForStore.filter((record) => record.periodEnd >= previousStart && record.periodEnd <= previousEnd),
    };
  }, [customEnd, customStart, recordsForStore]);
  const buckets = trendMode === "custom" ? [customBucket] : regularBuckets;
  const activePeriod = trendMode === "custom" ? customBucket.key : (period || buckets[0]?.key || "");
  const activeIndex = Math.max(0, buckets.findIndex((bucket) => bucket.key === activePeriod));
  const activeBucket = trendMode === "custom" ? customBucket : buckets[activeIndex];
  const previousBucket = trendMode === "custom" ? customPreviousBucket : buckets[activeIndex + 1];
  const periodRecords = activeBucket?.records ?? [];
  const previousSummary = useMemo(() => summarize(previousBucket?.records ?? []), [previousBucket]);
  const filteredRecords = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term
      ? periodRecords.filter((record) => `${record.storeName}${record.agentName}${record.subAccountName}`.toLowerCase().includes(term))
      : periodRecords;
  }, [periodRecords, search]);
  const summary = useMemo(() => summarize(periodRecords), [periodRecords]);
  const storeRows = useMemo(() => {
    const groups = new Map<string, DouyinPerformanceRecord[]>();
    periodRecords.forEach((record) => groups.set(record.storeName, [...(groups.get(record.storeName) ?? []), record]));
    return [...groups.entries()].map(([storeName, records]) => ({
      storeName,
      agents: new Set(records.map((record) => record.subAccountName || record.agentName)).size,
      sources: [...new Set(records.map((record) => record.dataSource))],
      summary: summarize(records),
    })).sort((a, b) => b.summary.receptionCount - a.summary.receptionCount);
  }, [periodRecords]);
  const agentRows = useMemo(() => [...filteredRecords].sort((a, b) => {
    const difference = a[selectedMetric] - b[selectedMetric];
    return selectedMetric === "satisfactionRate" ? difference : -difference;
  }), [filteredRecords, selectedMetric]);
  const attentionRows = periodRecords.filter((record) => (record.satisfactionRate > 0 && record.satisfactionRate < 95) || record.avgReplySeconds > 15 || record.dissatisfactionRate > 3);
  const bestConversion = [...periodRecords].sort((a, b) => b.inquiryConversionRate - a.inquiryConversionRate)[0];
  const bestSatisfaction = [...periodRecords].sort((a, b) => b.satisfactionRate - a.satisfactionRate)[0];
  const coveredStores = new Set(periodRecords.map((record) => record.storeName)).size;
  const missingDays = missingCollectionDays(data?.batches ?? []);
  const hasDemoData = data?.records.some((record) => record.dataSource === "DEMO") ?? false;
  const trendBuckets = trendMode === "custom" ? buildBuckets(periodRecords, "day").slice(0, 8).reverse() : buckets.slice(0, 8).reverse();
  const trendSummaries = trendBuckets.map((bucket) => summarize(bucket.records));
  const historyBatches = [...(data?.batches ?? [])]
    .filter((batch) => storeFilter === "ALL" || batch.storeName === storeFilter)
    .sort((a, b) => b.collectedAt.localeCompare(a.collectedAt));

  if (dashboard.isLoading) return <main className="main"><div className="centerState"><RefreshCw className="spin" />正在整理抖音数据...</div></main>;
  if (dashboard.isError || !data) return <main className="main"><div className="centerState errorState"><AlertTriangle />无法读取抖音数据<button className="button" onClick={() => void dashboard.refetch()}>重新加载</button></div></main>;

  const startCollection = () => {
    if (!data.sync.enabled) {
      window.open("https://fxg.jinritemai.com/login/common?channel=zhaoshang", "_blank", "noopener,noreferrer");
    }
    syncMutation.mutate();
  };

  const selectMetric = (metric: MetricKey) => {
    setSelectedMetric(metric);
    setView("overview");
    setMetricOpen(metric);
  };

  const metricValue = (record: Summary | DouyinPerformanceRecord, metric: MetricKey) => metric === "receptionCount" ? formatNumber(record.receptionCount) : metric === "netSalesAmount" ? formatMoney(record.netSalesAmount) : metric === "avgReplySeconds" ? `${formatDecimal(record.avgReplySeconds)} 秒` : `${formatDecimal(record[metric])}%`;

  const metricCardProps = (metric: MetricKey, className = "") => ({
    className: `${className} ${selectedMetric === metric ? "selected" : ""}`.trim(),
    role: "button" as const,
    tabIndex: 0,
    "aria-label": `查看${metricLabels[metric]}客服明细`,
    onClick: () => selectMetric(metric),
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        selectMetric(metric);
      }
    },
  });
  const selectTrendMode = (mode: TrendSelection) => {
    setTrendMode(mode);
    setPeriod("");
    if (mode === "custom" && !customRange.start && !customRange.end) {
      setCustomRange({ start: defaultCustomStart, end: latestDate });
    }
  };

  return <main className="main douyinMain">
    <header className="topbar">
      <button className="menuButton" aria-label="打开导航" onClick={onMenuOpen}><Menu /></button>
      <strong>抖音客服绩效</strong><span>老板经营视图</span>
      <i className={data.sync.enabled ? "liveDot" : "offlineDot"} />
      <small>{data.sync.enabled ? "自动采集" : "飞鸽导入"}</small>
    </header>
    <div className="douyinContent">
      {platformNav}
      <section className="douyinHead">
        <div><h1>抖音客服经营数据</h1><p>集中查看 34 家店铺的接待、满意度、响应与询单转化</p></div>
        <div className="douyinActions">
          <button className="button" onClick={() => exportCsv(periodRecords)}><Download />导出整理表</button>
          <button className="button" onClick={() => setImportOpen(true)}><FileUp />导入飞鸽数据</button>
          <button className="button dangerButton" disabled={!hasDemoData || clearDemoMutation.isPending} onClick={() => {
            if (window.confirm("仅清除全部演示/模拟数据，CSV 导入、手工和自动采集的真实数据都会保留。确认继续？")) clearDemoMutation.mutate();
          }}><Trash2 />{clearDemoMutation.isPending ? "清除中..." : "清除全部演示数据"}</button>
          <button className="button primary" disabled={syncMutation.isPending} onClick={startCollection}>
            {data.sync.enabled ? <RefreshCw className={syncMutation.isPending ? "spin" : ""} /> : <ExternalLink />}
            {syncMutation.isPending ? "采集中..." : data.sync.enabled ? "立即自动采集" : "打开采集窗口"}
          </button>
        </div>
      </section>

      <section className={`douyinSyncBand ${data.sync.enabled ? "connected" : ""}`}>
        <Bot />
        <div><strong>{data.sync.enabled ? "自动采集已启用" : "浏览器登录采集模式"}</strong><span>{data.sync.message} · 最近更新 {formatDateTime(data.lastSyncedAt)}</span></div>
        <span><History />{data.batches.length} 个批次</span>
      </section>

      <section className="douyinToolbar">
        <div className="douyinViewTabs" role="tablist">
          <button className={view === "overview" ? "active" : ""} onClick={() => setView("overview")}>总览与明细</button>
          <button className={view === "history" ? "active" : ""} onClick={() => setView("history")}>采集记录</button>
        </div>
        <div className="douyinTrendModes" role="tablist" aria-label="趋势周期">{(["day", "month", "custom"] as TrendSelection[]).map((mode) => <button key={mode} className={trendMode === mode ? "active" : ""} onClick={() => selectTrendMode(mode)}>{mode === "day" ? "日趋势" : mode === "month" ? "月趋势" : "自定义"}</button>)}</div>
        {trendMode === "custom" ? <div className="douyinCustomRange"><label>开始日期<input type="date" min={earliestDate} max={latestDate} value={customStart} onChange={(event) => { const start = event.target.value; setCustomRange((range) => ({ start, end: range.end && range.end < start ? start : (range.end || customEnd) })); }} /></label><span>至</span><label>结束日期<input type="date" min={earliestDate} max={latestDate} value={customEnd} onChange={(event) => { const end = event.target.value; setCustomRange((range) => ({ start: range.start && range.start > end ? end : (range.start || customStart), end })); }} /></label></div> : <label>统计周期<select value={activePeriod} onChange={(event) => setPeriod(event.target.value)}>{buckets.map((bucket) => <option key={bucket.key} value={bucket.key}>{bucket.label}</option>)}</select></label>}
        <label>店铺<select value={storeFilter} onChange={(event) => { setStoreFilter(event.target.value); setPeriod(""); }}><option value="ALL">全部店铺</option>{data.stores.map((store) => <option key={store.id} value={store.name}>{store.name}</option>)}</select></label>
      </section>

      {view === "overview" && <>
        <section className="douyinKpis">
          <article {...metricCardProps("receptionCount")}><div><span>已接待人数</span><Users /></div><strong>{formatNumber(summary.receptionCount)}</strong>{previousBucket ? <Delta current={summary.receptionCount} previous={previousSummary.receptionCount} /> : <small>{periodRecords.length} 条客服绩效记录</small>}</article>
          <article {...metricCardProps("inquiryConversionRate", "featured")}><div><span>询单转化率</span><TrendingUp /></div><strong>{formatDecimal(summary.inquiryConversionRate)}%</strong>{previousBucket ? <Delta current={summary.inquiryConversionRate} previous={previousSummary.inquiryConversionRate} points /> : <small>按接待人数加权</small>}</article>
          <article {...metricCardProps("satisfactionRate")}><div><span>工作时间满意率</span><CheckCircle2 /></div><strong>{formatDecimal(summary.satisfactionRate)}%</strong>{previousBucket ? <Delta current={summary.satisfactionRate} previous={previousSummary.satisfactionRate} points /> : <small>目标建议不低于 95%</small>}</article>
          <article {...metricCardProps("dissatisfactionRate")}><div><span>工作时间不满意率</span><AlertTriangle /></div><strong>{formatDecimal(summary.dissatisfactionRate)}%</strong><small>目标建议不高于 3%</small></article>
          <article {...metricCardProps("avgReplySeconds")}><div><span>全天平均响应</span><Timer /></div><strong>{formatDecimal(summary.avgReplySeconds)}<em>秒</em></strong>{previousBucket ? <Delta current={summary.avgReplySeconds} previous={previousSummary.avgReplySeconds} inverse /> : <small>目标建议不超过 15 秒</small>}</article>
          <article {...metricCardProps("netSalesAmount")}><div><span>净销售额</span><WalletCards /></div><strong>{formatMoney(summary.netSalesAmount)}</strong>{previousBucket ? <Delta current={summary.netSalesAmount} previous={previousSummary.netSalesAmount} /> : <small>飞鸽退款后销售额</small>}</article>
        </section>

        <section className="douyinTrendPanel">
          <header><div><h2>{trendMode === "day" ? "日" : trendMode === "week" ? "周" : trendMode === "month" ? "月" : "自定义周期"}趋势与上期变化</h2><p>展示最近 {trendBuckets.length} 个周期，按当前店铺筛选汇总</p></div><span>{activeBucket?.label ?? "暂无周期"}</span></header>
          <div className="douyinTrendGrid">
            <TrendMetric label="接待人数" values={trendSummaries.map((item) => item.receptionCount)} current={summary.receptionCount} previous={previousSummary.receptionCount} format={formatNumber} />
            <TrendMetric label="询单转化" values={trendSummaries.map((item) => item.inquiryConversionRate)} current={summary.inquiryConversionRate} previous={previousSummary.inquiryConversionRate} format={(value) => `${formatDecimal(value)}%`} points />
            <TrendMetric label="平均响应" values={trendSummaries.map((item) => item.avgReplySeconds)} current={summary.avgReplySeconds} previous={previousSummary.avgReplySeconds} format={(value) => `${formatDecimal(value)}秒`} inverse />
            <TrendMetric label="满意度" values={trendSummaries.map((item) => item.satisfactionRate)} current={summary.satisfactionRate} previous={previousSummary.satisfactionRate} format={(value) => `${formatDecimal(value)}%`} points />
            <TrendMetric label="净销售额" values={trendSummaries.map((item) => item.netSalesAmount)} current={summary.netSalesAmount} previous={previousSummary.netSalesAmount} format={formatMoney} />
          </div>
          <footer>{trendBuckets.map((bucket) => <span key={bucket.key}>{bucket.label}</span>)}</footer>
        </section>

        <section className="douyinAnalysis">
          <header><div><h2>老板摘要</h2><p>按当前周期自动识别亮点、风险和数据完整度</p></div><span>需关注 {attentionRows.length} 条</span></header>
          <div>
            <article className="success"><TrendingUp /><div><strong>转化领先</strong><p>{bestConversion ? `${bestConversion.agentName}的询单转化率为 ${formatDecimal(bestConversion.inquiryConversionRate)}%，来自${bestConversion.storeName}。` : "暂无可分析数据。"}</p></div></article>
            <article className="info"><BarChart3 /><div><strong>服务表现最佳</strong><p>{bestSatisfaction ? `${bestSatisfaction.agentName}满意率 ${formatDecimal(bestSatisfaction.satisfactionRate)}%，平均响应 ${formatDecimal(bestSatisfaction.avgReplySeconds)} 秒。` : "暂无可分析数据。"}</p></div></article>
            <article className={attentionRows.length ? "danger" : "success"}><AlertTriangle /><div><strong>{attentionRows.length ? `${attentionRows.length} 条绩效需要关注` : "服务指标整体稳定"}</strong><p>{attentionRows.length ? `${attentionRows.slice(0, 3).map((record) => record.agentName).join("、")}存在满意率、响应或不满意率异常。` : "当前数据未发现超过建议阈值的项目。"}</p></div></article>
            <article className={coveredStores === data.stores.length ? "success" : "warning"}><Store /><div><strong>数据完整度 {Math.round(coveredStores / data.stores.length * 100)}%</strong><p>本周期已覆盖 {coveredStores} 家店铺，仍有 {data.stores.length - coveredStores} 家待采集。</p></div></article>
          </div>
        </section>

        <section className="douyinPanel">
          <header><div><h2>店铺经营汇总</h2><p>同一周期横向比较各店铺客服表现</p></div></header>
          <div className="douyinStoreRows">{storeRows.map((row) => {
            const warning = row.summary.satisfactionRate < 95 || row.summary.avgReplySeconds > 15;
            return <article key={row.storeName}>
              <div className="douyinStoreName"><span>{row.storeName.slice(0, 1)}</span><div><strong>{row.storeName}</strong><small>{row.agents} 名客服</small></div></div>
              <div><span>数据来源</span><SourceTags sources={row.sources} /></div>
              <div><span>接待人数</span><strong>{formatNumber(row.summary.receptionCount)}</strong></div>
              <div><span>询单转化</span><strong>{formatDecimal(row.summary.inquiryConversionRate)}%</strong></div>
              <div><span>满意率</span><strong>{formatDecimal(row.summary.satisfactionRate)}%</strong></div>
              <div><span>平均响应</span><strong>{formatDecimal(row.summary.avgReplySeconds)} 秒</strong></div>
              <span className={`douyinStatus ${warning ? "warning" : "success"}`}>{warning ? "需关注" : "稳定"}</span>
            </article>;
          })}</div>
        </section>
      </>}

      {view === "overview" && <section ref={detailsRef} className="douyinPanel douyinTablePanel">
        <header><div><h2>客服绩效明细</h2><p>当前按{metricLabels[selectedMetric]}{selectedMetric === "satisfactionRate" ? "从低到高" : "从高到低"}排列</p></div><label className="douyinSearch"><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索店铺、客服或子账号" /></label></header>
        <div className="douyinTableScroll"><table><thead><tr><th>店铺 / 客服</th><th>子账号名称</th><th>数据来源</th><th>已接待人数</th><th>不满意率</th><th>满意率</th><th>全天平响时长</th><th>询单转化率</th><th>净销售额</th><th>状态</th></tr></thead><tbody>{agentRows.map((record) => {
          const warning = (record.satisfactionRate > 0 && record.satisfactionRate < 95) || record.avgReplySeconds > 15 || record.dissatisfactionRate > 3;
          return <tr key={record.id}><td><strong>{record.agentName}</strong><small>{record.storeName}</small></td><td><strong>{record.subAccountName || "未提供"}</strong></td><td><SourceTag source={record.dataSource} /></td><td>{formatNumber(record.receptionCount)}</td><td className={record.dissatisfactionRate > 3 ? "dangerText" : ""}>{formatDecimal(record.dissatisfactionRate)}%</td><td className={record.satisfactionRate > 0 && record.satisfactionRate < 95 ? "dangerText" : ""}>{record.satisfactionRate ? `${formatDecimal(record.satisfactionRate)}%` : "无评价"}</td><td className={record.avgReplySeconds > 15 ? "warningText" : ""}>{formatDecimal(record.avgReplySeconds)} 秒</td><td><strong className="douyinMetric">{formatDecimal(record.inquiryConversionRate)}%</strong></td><td>{formatMoney(record.netSalesAmount)}</td><td><span className={`douyinStatus ${warning ? "warning" : "success"}`}>{warning ? "需关注" : "稳定"}</span></td></tr>;
        })}</tbody></table>{agentRows.length === 0 && <div className="operationsEmpty">当前筛选条件下没有数据</div>}</div>
        <footer><span>共 {agentRows.length} 条明细</span><span>已按{metricLabels[selectedMetric]}排序</span></footer>
      </section>}

      {view === "history" && <section className="douyinPanel douyinTablePanel">
        <header><div><h2>采集记录</h2><p>每次自动采集或文件导入均保留独立批次</p></div><span className={`douyinHistoryHealth ${missingDays?.length ? "warning" : ""}`}>{missingDays === null ? <History /> : missingDays.length ? <AlertTriangle /> : <CheckCircle2 />}{missingDays === null ? "尚无连续日采集记录" : missingDays.length ? `近14天缺失 ${missingDays.length} 次：${missingDays.map((date) => bucketLabel(date, "day")).join("、")}` : "近14天采集完整"}</span></header>
        <div className="douyinTableScroll"><table><thead><tr><th>采集批次</th><th>采集时间</th><th>批次行数</th><th>导入文件名</th></tr></thead><tbody>{historyBatches.slice(0, 100).map((batch) => <tr key={batch.id}><td><SourceTag source={batch.dataSource} /><small>{batch.id}</small></td><td>{formatDateTime(batch.collectedAt)}</td><td>{batch.rowCount} 行</td><td>{batch.importFileName ?? "自动采集，无文件"}</td></tr>)}</tbody></table></div>
        <footer><span>共 {historyBatches.length} 个采集批次</span><span>{historyBatches.length > 100 ? "当前显示最近 100 个" : "已显示全部批次"}</span></footer>
      </section>}
    </div>

    {metricOpen && <MetricDetailModal title={metricLabels[metricOpen]} period={activeBucket?.label ?? "当前周期"} total={metricValue(summary, metricOpen)} rows={periodRecords.map((record) => ({ key: record.id, name: record.agentName, store: record.subAccountName ? `${record.storeName} · ${record.subAccountName}` : record.storeName, value: metricValue(record, metricOpen) }))} onClose={() => setMetricOpen(null)} />}
    {importOpen && <div className="modalLayer"><section className="douyinImportModal" role="dialog" aria-modal="true" aria-labelledby="douyin-import-title">
      <header><div><h2 id="douyin-import-title">提取飞鸽客服数据</h2><span>选择店铺与统计周期，系统自动整理飞鸽导出的 CSV</span></div><button className="iconButton" aria-label="关闭" onClick={() => setImportOpen(false)}><X /></button></header>
      <div className="douyinImportForm">
        <label>所属店铺<select value={importForm.storeName} onChange={(event) => setImportForm((form) => ({ ...form, storeName: event.target.value }))}>{data.stores.map((store) => <option key={store.id}>{store.name}</option>)}</select></label>
        <label>开始日期<input type="date" value={importForm.periodStart} onChange={(event) => setImportForm((form) => ({ ...form, periodStart: event.target.value }))} /></label>
        <label>结束日期<input type="date" value={importForm.periodEnd} onChange={(event) => setImportForm((form) => ({ ...form, periodEnd: event.target.value }))} /></label>
        <button className={`douyinDropzone ${importForm.fileName ? "selected" : ""}`} onClick={() => importRef.current?.click()}><FileSpreadsheet /><strong>{importForm.fileName || "选择飞鸽导出的 CSV"}</strong><span>{importForm.fileName ? "文件已就绪" : "支持 CSV 或制表符文本"}</span></button>
        <input ref={importRef} hidden type="file" accept=".csv,.txt,text/csv,text/plain" onChange={async (event) => { const file = event.target.files?.[0]; if (!file) return; const csv = await file.text(); setImportForm((form) => ({ ...form, fileName: file.name, csv })); }} />
        <aside><CheckCircle2 /><span>自动识别客服昵称、子账号名称、已接待人数、工作时间满意/不满意率、全天平响时长、询单转化率和退款后销售额。</span></aside>
      </div>
      <footer><button className="button" onClick={() => setImportOpen(false)}>取消</button><button className="button primary" disabled={importMutation.isPending} onClick={() => { if (!importForm.fileName || !importForm.csv) { setNotice("请先选择飞鸽导出的 CSV 文件"); return; } importMutation.mutate(importForm); }}>{importMutation.isPending ? "正在提取..." : "提取并生成表格"}</button></footer>
    </section></div>}
    {notice && <button className="toast" onClick={() => setNotice(null)}>{notice}</button>}
  </main>;
}
